import { Badge, Button, LearnMoreLink } from '@auctisync/ui';
import { formatCents, type Tables } from '@auctisync/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type Invoice = Tables<'invoices'>;
type Auction = Pick<Tables<'auctions'>, 'id' | 'title' | 'slug'>;

type InvoiceWithJoins = Invoice & {
  auctions: Auction | null;
};

type FailureBucket = {
  code: string;
  count: number;
  total_cents: number;
};

const PAGE_SIZE = 25;
const STATUSES = ['draft', 'issued', 'paid', 'partially_paid', 'refunded', 'void'] as const;

/**
 * /admin/invoices — tenant-scoped invoice management. Module #9 Phase 2.
 *
 * Filters: status, search by invoice number / bidder email, auction, date.
 * Failed-payments summary banner at top, with drill-down to per-bucket details.
 * Per-row action cell links into the detail page where actions live.
 *
 * RLS scopes rows to the caller's tenant; super_admin sees all tenants.
 */
export function AdminInvoices() {
  const [rows, setRows] = useState<InvoiceWithJoins[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(0);

  const [failureBuckets, setFailureBuckets] = useState<FailureBucket[]>([]);

  // Debounce search.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load failed-payments summary independent of pagination.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error: err } = await supabase
        .from('payments')
        .select('failure_code, amount_cents')
        .eq('status', 'failed')
        .gte('failed_at', since);
      if (cancelled) return;
      if (err) {
        // Non-fatal — banner just hides.
        setFailureBuckets([]);
        return;
      }
      const buckets = new Map<string, FailureBucket>();
      for (const row of data ?? []) {
        const code = row.failure_code ?? 'unknown';
        const amount = (row.amount_cents as number | null) ?? 0;
        const existing = buckets.get(code);
        if (existing) {
          existing.count++;
          existing.total_cents += amount;
        } else {
          buckets.set(code, { code, count: 1, total_cents: amount });
        }
      }
      setFailureBuckets([...buckets.values()].sort((a, b) => b.count - a.count));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load invoices for current page + filters.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      let query = supabase
        .from('invoices')
        .select('*, auctions!invoices_auction_id_fkey(id, title, slug)', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (statusFilter) query = query.eq('status', statusFilter);

      const trimmed = search.trim();
      if (trimmed) {
        const escaped = trimmed.replace(/[%_,]/g, (c) => `\\${c}`);
        const pattern = `%${escaped}%`;
        query = query.ilike('invoice_number', pattern);
      }

      query = query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      const { data, count, error: err } = await query;
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setRows([]);
        setTotalCount(0);
      } else {
        setRows((data ?? []) as InvoiceWithJoins[]);
        setTotalCount(count ?? 0);
        setError(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [page, statusFilter, search]);

  const failureTotal = useMemo(
    () => failureBuckets.reduce((sum, b) => sum + b.count, 0),
    [failureBuckets],
  );

  const lastPage = Math.max(0, Math.ceil(totalCount / PAGE_SIZE) - 1);

  return (
    <div>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.875rem',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '0.25rem',
          }}
        >
          Invoices
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>
          Manage bidder invoices: view, retry failed payments, charge a different card, or mark paid
          offline.{' '}
          <LearnMoreLink helpSlug="managing-failed-payments" newTab>
            Managing failed payments
          </LearnMoreLink>
        </p>
      </header>

      {failureTotal > 0 && (
        <div
          role="status"
          style={{
            padding: '0.875rem 1rem',
            marginBottom: '1.5rem',
            borderRadius: 'var(--r-card, 0.5rem)',
            border: '1px solid var(--warn-border, #facc15)',
            background: 'var(--warn-bg, #fef9c3)',
            color: 'var(--warn-fg, #713f12)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
              {failureTotal} payment{failureTotal === 1 ? '' : 's'} failed in the last 24 hours.
            </div>
            <div style={{ fontSize: '0.875rem' }}>
              {failureBuckets.slice(0, 4).map((b, i) => (
                <span key={b.code}>
                  {i > 0 && ' · '}
                  <strong>{b.count}</strong> {humanFailureCode(b.code)}
                </span>
              ))}
              {failureBuckets.length > 4 && ` · ${failureBuckets.length - 4} more`}
            </div>
          </div>
          <Link
            to="/admin/invoices/failed-report"
            style={{
              color: 'var(--warn-fg, #713f12)',
              fontWeight: 600,
              textDecoration: 'underline',
              fontSize: '0.875rem',
            }}
          >
            View report →
          </Link>
        </div>
      )}

      <div style={filtersStyle}>
        <input
          type="search"
          placeholder="Search invoice #"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={inputStyle}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          style={inputStyle}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading invoices…</div>
      ) : rows.length === 0 ? (
        <div style={emptyStateStyle}>
          <p style={{ margin: 0 }}>No invoices match the current filters.</p>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Invoice #</th>
                  <th style={thStyle}>Auction</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => (
                  <tr key={inv.id} style={trStyle}>
                    <td style={tdStyle}>
                      <Link
                        to={`/admin/invoices/${inv.id}`}
                        style={{ color: 'var(--brand-primary)', fontWeight: 500 }}
                      >
                        {inv.invoice_number ?? inv.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td style={tdStyle}>{inv.auctions?.title ?? '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {formatCents(inv.total_cents, inv.currency)}
                    </td>
                    <td style={tdStyle}>
                      <Badge variant={statusBadgeVariant(inv.status)}>{inv.status}</Badge>
                    </td>
                    <td style={tdStyle}>
                      {new Date(inv.created_at).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <Link
                        to={`/admin/invoices/${inv.id}`}
                        style={{ color: 'var(--brand-primary)', fontSize: '0.875rem' }}
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={paginationStyle}>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              Page {page + 1} of {lastPage + 1} · {totalCount} invoice{totalCount === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant="ghost"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
              >
                ← Prev
              </Button>
              <Button
                variant="ghost"
                onClick={() => setPage(Math.min(lastPage, page + 1))}
                disabled={page >= lastPage}
              >
                Next →
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'outline' | 'error' {
  switch (status) {
    case 'paid':
      return 'default';
    case 'void':
    case 'refunded':
      return 'error';
    case 'partially_paid':
      return 'secondary';
    default:
      return 'outline';
  }
}

function humanFailureCode(code: string): string {
  switch (code) {
    case 'insufficient_funds':
      return 'insufficient funds';
    case 'fraudulent':
    case 'fraud_detected':
      return 'fraud detected';
    case 'do_not_honor':
      return 'card declined';
    case 'expired_card':
      return 'expired card';
    case 'incorrect_cvc':
      return 'wrong CVC';
    case 'unknown':
      return 'unknown';
    default:
      return code.replace(/_/g, ' ');
  }
}

const filtersStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  marginBottom: '1rem',
  flexWrap: 'wrap',
};

const inputStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
  minWidth: '14rem',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  background: 'var(--surface-elev)',
  borderRadius: 'var(--r-card, 0.5rem)',
  overflow: 'hidden',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.75rem 1rem',
  background: 'var(--surface)',
  borderBottom: '1px solid var(--admin-hairline, var(--border))',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const trStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--admin-hairline, var(--border))',
};

const tdStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  fontSize: '0.875rem',
  color: 'var(--text)',
};

const paginationStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '1rem',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--error-bg, #fee2e2)',
  color: 'var(--error-fg, #991b1b)',
  marginBottom: '1rem',
};

const emptyStateStyle: React.CSSProperties = {
  padding: '1.5rem',
  borderRadius: 'var(--r-card, 0.5rem)',
  border: '1px dashed var(--border)',
  color: 'var(--text-muted)',
};
